/**
 * @module app.shared
 */

import { SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { deep } from 'app/shared/lib/deep';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';

export interface INestedLocalSorting {
    [index: number]: Sorting;
}

export function localSort(values: any[], sorting: Sorting): any[] {
    sorting.entries.forEach(entry => {
        if(!entry.convert) {
            entry.convert = function(value: any) {
                if(typeof value === 'string') {
                    return value ? value.toLowerCase() : '';
                }
                return value
            }
        }
    });

    return values.sort((value1: any | null | undefined, value2: any | null | undefined): number => {
        if(value1 instanceof Array && value2 instanceof Array) {
            value1 = value1[0];
            value2 = value2[0];
        }

        // if one of them doesn't exist, then we know how to sort
        let result = (value1 && !value2) ? 1 : (!value1 && value2) ? -1 : 0;
        if(result != 0) return result;

        if(value1 && value2) {
            for(let entry of sorting.entries.reverse()) {
                let property: string = entry.reference;
                let direction = entry.direction === SortDirection.Descending ? -1 : 1;
                let result = 0;

                // this lets us use indexing on deep properties
                value1 = deep(value1);
                value2 = deep(value2);
                let convertedValue1 = entry.convert ? entry.convert!(value1[property]) : value1[property];
                let convertedValue2 = entry.convert ? entry.convert!(value2[property]) : value2[property];

                // if one of them doesn't exist, then we know how to sort
                result = (convertedValue1 && !convertedValue2) ? 1 : (convertedValue2 && !convertedValue1) ? -1 : 0;
                if(result != 0) return result * direction;

                // we know how to sort strings and numbers
                if(typeof property.valueOf === 'function') {
                    result = (convertedValue1! < convertedValue2!) ? -1 : (convertedValue1! > convertedValue2!) ? 1 : 0;
                }

                // if we have a definitive way to sort, lets do that now; if not, lets keep sorting
                if(result != 0) return result * direction;
            };
        }

        return 0;
    });
}

/**
 * Applies a SortChangeEvent to a Sorting to create a new Sorting.
 * @param sorting - The original Sorting.
 * @param event - A SortChangeEvent to apply to Sorting.
 * @returns - A new Sorting.
 */
export function applySort(sorting: Sorting, event: SortChangeEvent): Sorting {
    const { property, direction } = event.detail;
    if(direction === null) {
        sorting.remove(property);
    } else {
        sorting.add({
            reference: property,
            direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
        });
    }
    return Object.assign(new Sorting(), sorting);
}
